/**
 * lazyWithRetry — wraps React.lazy() with auto-retry on transient chunk-load failures.
 *
 * IMPORTANT: This wrapper NEVER silently returns a blank component. If the
 * dynamic import fails after a quick retry, we re-throw the original error so
 * React.lazy + Suspense surface it to the nearest <ChunkErrorBoundary>, which
 * displays a clear "Reload app" UI instead of a blank white screen.
 *
 * The stale-service-worker class of issues is recovered separately by:
 *   1. The dead-man's switch in index.html (reloads if React root stays empty 6s)
 *   2. The ChunkErrorBoundary auto-reload on first chunk error (with loop guard)
 *   3. Network-first /index.html in service-worker.js
 */
import { lazy } from 'react';

/**
 * @param {() => Promise<{default: React.ComponentType}>} importer
 * @returns {React.LazyExoticComponent}
 */
export function lazyWithRetry(importer) {
  return lazy(() =>
    importer().catch(async (err) => {
      // One quick retry covers flaky network blips. If that also fails, throw
      // the original error so the ChunkErrorBoundary above can render a proper
      // recovery UI — never return a no-op component (which would manifest as
      // a permanent blank white screen for the user).
      await new Promise((r) => setTimeout(r, 400));
      return importer().catch(() => {
        throw err;
      });
    })
  );
}
